import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  Analysis,
  AnalysisList,
  Count,
  FirstCompletions,
  FirstCompletionsInput,
  MoveInput,
  RecentAnalysesInput,
  RecentAnalyses,
  RemoveInput,
  RenameInput,
  SearchTargetsInput,
  StartInput,
  TargetList,
  UpdateSettingsInput,
  UpdateWizardInput,
} from "./analysis.schemas.ts";

/**
 * Every call is scoped to the caller's active organization (ADR-001 §5): an
 * analysis belongs to the organization that ran it, not to `userId` alone,
 * which is kept only as the author of what it touches.
 */
type ForOrganization = { readonly userId: string; readonly organizationId: string };

/** The worker's last published view of a provider it calls, by name (ADR-002 point 8): only
 * "hiker" today. Peer-only, for the back office's compute page (S9.09); no transport door. */
export type ProviderHealthView = {
  readonly provider: string;
  readonly status: "ok" | "degraded" | "down";
  readonly trouble: "rate-limited" | "unavailable" | "refused" | null;
  readonly reason: string | null;
  readonly retryAt: string | null;
  readonly since: string | null;
};

export interface AnalysisApi extends EraseStep {
  /** The current project's analyses, with counts by status. */
  list(input: ForOrganization): Promise<AnalysisList>;
  /** The current project's newest analyses, newest first, at most `limit` (5 when absent); the
   * home's latest work (S39.01). Only these are read, never the whole list. */
  recent(input: ForOrganization & RecentAnalysesInput): Promise<RecentAnalyses>;
  get(input: ForOrganization & { readonly analysisId: string }): Promise<Analysis>;
  /** A new, empty analysis in the current project, at wizard step 1. */
  create(input: ForOrganization): Promise<Analysis>;
  updateWizard(input: ForOrganization & UpdateWizardInput): Promise<Analysis>;
  searchTargets(input: ForOrganization & SearchTargetsInput): Promise<TargetList>;
  start(input: ForOrganization & StartInput): Promise<Analysis>;
  pause(input: ForOrganization & { readonly analysisId: string }): Promise<Analysis>;
  rename(input: ForOrganization & RenameInput): Promise<Analysis>;
  updateSettings(input: ForOrganization & UpdateSettingsInput): Promise<Analysis>;
  remove(input: ForOrganization & RemoveInput): Promise<Count>;
  moveToProject(input: ForOrganization & MoveInput): Promise<Count>;
  /** Settings' purge: every analysis of the organization, in every project, with its prospects. */
  purge(input: ForOrganization): Promise<Count>;
  /** Operator-only (D053): no transport door. Stamps rows left over from before S1.03. */
  backfillOrganizationId(): Promise<{ readonly assigned: number }>;
  /** Operator-only (D956): no transport door. Every HikerAPI-driven analysis row reads its
   * `status`/`progress` live, from its run's fold (`#view`); this writes a stopped run's ending
   * back into the row itself, for one still showing "running, nothing yet" from before the row
   * synced itself on every read. Idempotent: a row that already agrees is left alone. */
  repairStuckRuns(): Promise<{ readonly repaired: number }>;
  /** Every provider's last published view, for the compute page (S9.09). Peer-only. */
  providerHealth(): Promise<readonly ProviderHealthView[]>;
  /** The back office's funnel (ADR-028 revision 1 point 2, S33.13): when each organization's
   * first analysis completed, by id; one with none is absent. At most 100 a call. Peer-only. */
  firstCompletions(input: FirstCompletionsInput): Promise<FirstCompletions>;
}

export const AnalysisApi = moduleApi<AnalysisApi>()("analysis");
